// Product-level certifications — the single client-side definition.
//
// These are credentials attached to a *product* (or the factory that makes it),
// stored on lca_product_assessments.social_impact as:
//   { product: [certId], productOther: '', certFiles: { [certId]: { name, size, path } } }
//
// This list used to be copy-pasted into every surface that rendered or offered
// a cert — AssessmentSummary, ProductModal, ProductResults and the PDF — and
// had drifted: ProductModal was missing `wrap` and `fsc`, so a supplier could
// see those badges on the results page but never attach them from the product
// modal. Everything client-side now reads from here.
//
// MUST stay in sync with server/services/productCertifications.js, which is the
// server's copy and the one that validates manufacturer certification requests.
// Create React App can't import across the client/src boundary, so the two
// files are unavoidably separate — but they are the only two. The request modal
// prefers the server's list over this one (GET /api/product-certifications) so
// the one surface where a wrong id becomes a validation error can't drift.
//
// Ids are also keys into CERT_LOGOS in ./certLogos — an id with no entry there
// falls back to a monogram badge rather than artwork.

// Claim-based credentials that render as logo-only decoration with no link.
// They never take a proof document. Every other cert here shows as soon as it's
// selected too, but a certificate is recommended (the editors say so) and makes
// the badge link to it.
export const NO_FILE_CERTS = ['fsc_pefc', 'aba'];

export const PRODUCT_CERTS = [
  { id: 'sedex', label: 'Sedex SMETA' },
  { id: 'bsci', label: 'BSCI' },
  { id: 'sa8000', label: 'SA8000' },
  { id: 'iso45001', label: 'ISO 45001' },
  { id: 'wrap', label: 'WRAP' },
  { id: 'fsc', label: 'FSC' },
  { id: 'oeko_tex', label: 'OEKO-TEX®' },
  { id: 'fsc_pefc', label: 'FSC/PEFC Certified' },
  { id: 'aba', label: 'ABA Certified' },
].map((c) => ({ ...c, requiresFile: !NO_FILE_CERTS.includes(c.id) }));

export const PRODUCT_CERT_LABELS = Object.fromEntries(PRODUCT_CERTS.map((c) => [c.id, c.label]));

/** Human label for a stored cert id, falling back to the raw id. */
export function productCertLabel(id) {
  return PRODUCT_CERT_LABELS[id] || id;
}
